import type { Ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter, START_LOCATION, type Router } from 'vue-router'
import { useStore } from 'vuex'
import type { IDataTable, IPersistenciaFiltros } from '@/modules/Core/interfaces'
import type {
  ISnapshotFiltros,
  ISnapshotFiltroValor
} from '@/modules/Core/store/filtros'

const TTL_DEFECTO = 30 * 60 * 1000 // 30 minutos
// Fuera del prefijo 'filtros:' que el módulo Vuex escanea como snapshots
const CLAVE_ORIGEN = 'filtrosRutaOrigen'
let trackingInstalado = false

interface IRutaDestino {
  path: string
  name?: string | null
}

// Registra en cada navegación la ruta desde la que se llegó. No se puede usar
// history.state.back porque con el botón atrás apunta a la entrada anterior de
// la pila, no a la ruta de la que realmente se viene. Se persiste en
// sessionStorage para que la detección sobreviva un refresh.
function instalarTrackingOrigen(router: Router) {
  if (trackingInstalado) return
  trackingInstalado = true
  router.afterEach((_to, from) => {
    if (from === START_LOCATION) return
    try {
      sessionStorage.setItem(
        CLAVE_ORIGEN,
        JSON.stringify({ path: from.path, name: from.name ?? null })
      )
    } catch {
      // ignore storage errors
    }
  })
}

function rutaOrigen(): IRutaDestino | null {
  try {
    const raw = sessionStorage.getItem(CLAVE_ORIGEN)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * Persiste los filtros, página y límite de una tabla mientras el usuario
 * navega dentro del flujo de la vista (ver/editar/crear registros) y los
 * restaura al volver. Si se entra a la vista desde cualquier otra ruta,
 * el snapshot se descarta y la tabla arranca limpia.
 *
 * Uso transparente vía CrudBasic declarando `persistencia` en el IDataTable,
 * o de forma independiente:
 *   usePersistenciaFiltros(tabla, { rutas: ['PacienteView', 'PacienteCreate'] })
 */
export function usePersistenciaFiltros<T>(
  tabla: Ref<IDataTable<T>> | Ref<IDataTable<T> | undefined>,
  opciones?: IPersistenciaFiltros
) {
  const config = opciones ?? tabla.value?.persistencia
  if (!config || !config.rutas?.length) return

  const route = useRoute()
  const store = useStore()
  instalarTrackingOrigen(useRouter())
  const key = config.key ?? ((route.name as string) || route.path)
  const ttl = config.ttl ?? TTL_DEFECTO

  // Cada entrada de `rutas` es un nombre de ruta (de routes.ts) o,
  // si empieza con '/', un prefijo de path
  const esRutaRelacionada = (destino?: IRutaDestino | null) =>
    !!destino && config.rutas.some(ruta =>
      ruta.startsWith('/') ? destino.path.startsWith(ruta) : destino.name === ruta
    )

  const restaurar = () => {
    const snapshot: ISnapshotFiltros | null = store.getters['filtros/snapshot'](key)
    if (!snapshot) return
    if (Date.now() - snapshot.savedAt > ttl || !esRutaRelacionada(rutaOrigen())) {
      store.commit('filtros/clearSnapshot', key)
      return
    }
    const t = tabla.value
    if (!t) return
    t.filtros.forEach(filtro => {
      const guardado = snapshot.filtros[filtro.name]
      if (!guardado) return
      filtro.value = guardado.value
      if (guardado.search !== undefined) {
        filtro.search = guardado.search
      }
    })
    t.pag = snapshot.pag || 1
    t.limit = snapshot.limit || t.limit
    config.onRestaurado?.()
    if (import.meta.env.DEV) {
      console.debug(`[usePersistenciaFiltros] snapshot restaurado para "${key}"`, snapshot)
    }
  }

  onBeforeRouteLeave(to => {
    const t = tabla.value
    if (!t) return
    if (!esRutaRelacionada({ path: to.path, name: (to.name as string) ?? null })) {
      store.commit('filtros/clearSnapshot', key)
      return
    }
    // Se guardan todos los valores (incluso null) para reproducir el estado
    // exacto por encima de los valores por defecto que defina la vista
    const valores: Record<string, ISnapshotFiltroValor> = {}
    t.filtros.forEach(filtro => {
      valores[filtro.name] = {
        value: filtro.value ?? null,
        ...(filtro.search !== undefined ? { search: filtro.search } : {})
      }
    })
    const snapshot: ISnapshotFiltros = {
      filtros: valores,
      pag: t.pag,
      limit: t.limit,
      savedAt: Date.now()
    }
    store.commit('filtros/setSnapshot', { key, snapshot })
  })

  restaurar()
}
